<template>
  <vx-card class="mt-base" :title="activityTitle">
    <h4 class="mb-5">Triangles and Trusses</h4>
    <p class="mb-5">
      In the last activity, we learned about the importance of triangles for engineers when building
      rigid structures.
    </p>
    <p class="mb-5">
      When engineers use a series of connected triangles to form a single structure, these are
      called trusses. We can find them in everything from bridges and roofs to the international
      space station.
    </p>
    <p class="mb-5">Here’s an example of a truss bridge with the different parts labelled.</p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/truss_bridges.jpg')"
      classes="2/3"
      alt="Truss Bridges: side-view diagram of a truss bridge made of connected triangles with its parts labelled. Top Chord is the upper horizontal member, Bottom Chord the lower horizontal member, Verticals are the upright members, Diagonals are the sloping members, and Joints are the points where members meet. Captioned Non Load Path Redundant."
    />
    <p class="mb-5">
      Trusses can be used in many different patterns, each with their own specific name. Here are
      some examples of trusses we can commonly find in roofs and bridges. Can you spot all the
      triangles?
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/triangles_2.png')"
      classes="2/3"
      alt="Twelve labelled truss patterns. Roof trusses (triangular outline): Fink, Fan, King Post (a single vertical post from apex to base), Howe, Pratt and Modified Queen. Bridge trusses (rectangular outline): Howe, Pratt, Warren (a zigzag of diagonals with no verticals), Parker (curved top chord), Baltimore (many small sub-divided triangles) and K (K-shaped bracing)."
    />

    <h4 class="mb-5">
      Task 1: Look at the above picture again and answer the following 4 questions.
    </h4>
    <p class="mb-5">How many triangles can you see in the:</p>

    <div v-for="(question, index) in questions1" :key="'q1_' + index">
      <h5 class="mb-5 questionlabel">{{ index }}. {{ question.question }}</h5>
      <radio-buttons-wrapper
        v-model="data['question_1_' + index]"
        :index="'q1_' + index"
        :question="question"
        :is-editable="isEditable"
        show-question="false"
      />
    </div>

    <h4 class="mb-5">Understanding Trusses</h4>
    <p class="mb-5">
      To find out much more about trusses, check out the following video. Be warned though, it goes
      into great depth on how to calculate load carried, you only need to watch until 4:02 to get
      the information and understanding you require for now.
    </p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">Task 2: After watching the video, answer the following 3 questions.</h4>
    <div v-for="(question, index) in questions2" :key="'q2_' + index">
      <h5 class="mb-5 questionlabel">{{ index }}. {{ question.question }}</h5>
      <centered-image-wrapper
        :image="require('@/assets/images/experiences/engineering/' + question.image)"
        :alt="question.imageAlt || ''"
        v-if="typeof question.image !== 'undefined' && question.image !== ''"
      />

      <radio-buttons-wrapper
        v-model="data['question_2_' + index]"
        :index="'q2_' + index"
        :question="question"
        :is-editable="isEditable"
        show-question="false"
      />
    </div>

    <p class="mb-5">
      <strong>Compression</strong> and <strong> tension</strong> are two very important forces to
      think about when designing bridges.
    </p>
    <p class="mb-5">
      Compression (a force that pushes or squeezes inward) is carefully balanced with tension (a
      force that stretches and pulls outward).
    </p>
    <p class="mb-5">Take a look at the following diagram to see how this works more clearly.</p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/tension.png')"
      classes="2/3"
      alt="Diagram of a truss bridge over water, with members coloured red for tension and green for compression. The top chord, the sloping end members and the vertical members are green (compression); the bottom chord and the diagonal members are red (tension)."
    />

    <p class="mb-5">
      Now, watch this video to gain more insight into how these different forces combine to make a
      sturdy bridge. You’ll need this knowledge to be successful in the game that follows.
    </p>
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />

    <h4 class="mb-5">
      Task 3: Create at least 3 different bridges to allow a truck to cross a canyon.
    </h4>
    <p class="mb-5">
      Games can be a great way to practise your engineering design skills. In this game, a truck is
      trying to make a few deliveries, but it keeps encountering a series of dangerous canyons.
    </p>
    <p class="mb-5">
      Can you build some bridges across them so it can continue its journey in this online
      construction game? All you need to do is connect the dots! Here’s a reminder of some different
      types of truss bridges to help, but you can also use your own designs too. But, be careful, if
      your bridge isn’t built sturdy enough the truck will crash into the canyon below.
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/triangles_2.png')"
      classes="2/3"
      alt="Twelve labelled truss patterns. Roof trusses (triangular outline): Fink, Fan, King Post, Howe, Pratt and Modified Queen. Bridge trusses (rectangular outline): Howe, Pratt, Warren, Parker (curved top chord), Baltimore and K."
    />
    <p class="mb-5">
      If your bridge is sturdy enough and the truck makes it across to the other side, you’ll go
      onto the next, more difficult level - but be careful, you’re working to a budget too!
    </p>
    <p class="mb-5">
      To check out some more complicated truss bridge designs to help in this game, open this PDF:
    </p>
    <button-link-wrapper
      text="Open Truss Designs PDF"
      :link="'content/engineering/truss_bridge_designs.pdf' | blob_url"
    />

    <p class="mb-5">
      Click the image below to play the “Construct a Bridge” game now. There is also a “walkthrough”
      video underneath the game if you need some help.
    </p>
    <a href="https://www.agame.com/game/construct-a-bridge" target="_blank" class="mb-5">
      <centered-image-wrapper
        alt="Play the Construct a Bridge game (opens in a new tab)"
        :image="require('@/assets/images/experiences/engineering/construct_a_bridge.jpg')"
        classes="2/3"
      />
    </a>

    <p class="mb-5">What level of the game did you get up to?</p>
    <text-block v-model="data.question3" :editable="isEditable" />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '../../components/ActivitiesBlocks/RadioButtonsWrapper';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/what_is_a_truss.jpg'),
          video: 'https://www.youtube.com/embed/Hn_iozUo9m4',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/structures.jpg'),
          video: 'https://www.youtube.com/embed/7FqteQu43qU',
        },
      },
      image1: require('@/assets/images/experiences/gamesdeveloper/activity_1_1.png'),
      questions1: {
        1: {
          question: 'King Post roof truss?',
          answers: [
            { val: 1, answer: 1, correct: false },
            { val: 2, answer: 2, correct: false },
            { val: 3, answer: 3, correct: true },
            { val: 4, answer: 4, correct: false },
          ],
        },
        2: {
          question: 'Pratt roof truss?',
          answers: [
            { val: 5, answer: 1, correct: false },
            { val: 6, answer: 6, correct: false },
            { val: 7, answer: 12, correct: false },
            { val: 8, answer: 'At least 12', correct: true },
          ],
        },
        3: {
          question: 'Howe bridge truss?',
          answers: [
            { val: 9, answer: 3, correct: false },
            { val: 10, answer: 7, correct: false },
            { val: 11, answer: 11, correct: true },
            { val: 12, answer: 13, correct: false },
          ],
        },
        4: {
          question: 'Baltimore bridge truss',
          answers: [
            { val: 13, answer: 9, correct: false },
            { val: 14, answer: 18, correct: false },
            { val: 15, answer: 27, correct: false },
            { val: 16, answer: 'At least 27', correct: true },
          ],
        },
      },

      questions2: {
        1: {
          question: 'What is this?',
          image: 'q1.png',
          imageAlt:
            'Photo of a galvanised steel bracket made of flat plates joined at right angles, with several bolt holes.',

          answers: [
            { val: 1, answer: 'A caliper', correct: false },
            { val: 2, answer: 'A gusset plate', correct: true },
            { val: 3, answer: 'A strut', correct: false },
          ],
        },
        2: {
          question:
            'Look at the following diagram. Which letters describe where the load is operating?',
          image: 'diagram.jpg',
          imageAlt:
            'Diagram of a triangle. Point A is at the top, points E and G are the bottom-left and bottom-right corners, and point F is the midpoint of the base. The left side from E to A is labelled B, the right side from A to G is labelled D, and a vertical line from A down to F is labelled C.',

          answers: [
            { val: 5, answer: 'A, B, C, D', correct: false },
            { val: 6, answer: 'A, E, F, G', correct: true },
            { val: 7, answer: 'A, B, C, D, E, F, G', correct: false },
          ],
        },
        3: {
          question: 'Can you add one line to the shape below to make it more stable?',
          image: 'activity_2_question3.png',
          imageAlt: 'A blue parallelogram leaning to the right, with no internal lines.',
          answerWithImage: true,
          answers: [
            {
              val: 8,
              image: require('@/assets/images/experiences/engineering/chord_3.jpg'),
              alt: 'The parallelogram with a red line from the top edge to the bottom edge, parallel to the slanted sides.',
              correct: false,
              answerWithImage: true,
            },
            {
              val: 9,
              image: require('@/assets/images/experiences/engineering/chord_2.jpg'),
              alt: 'The parallelogram with a horizontal red line across the middle, parallel to the top and bottom edges.',
              correct: false,
              answerWithImage: true,
            },
            {
              val: 10,
              image: require('@/assets/images/experiences/engineering/chord_1.jpg'),
              alt: 'The parallelogram with a red diagonal line from the bottom-left corner to the top-right corner.',
              correct: true,
              answerWithImage: true,
            },
          ],
        },
      },
      questions3: '',

      img10: require('@/assets/images/experiences/engineering/what_is_a_truss.jpg'),
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  components: { RadioButtonsWrapper },
};
</script>

<style>
.questionlabel {
  line-height: 1.5;
}
label.isbar_label span {
  color: #d40000;
  font-weight: bold;
}
.checkbox_answer {
  float: left;
  margin: 5px 10px 0 0;
}
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
</style>
